import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCustomers } from "@/customers/useCustomers";
import { Filter } from "lucide-react";
import { useEffect, useState } from "react";

interface ApprovedResultsFilterPopoverProps {
  customerIds: string[];
  fromDate: Date | null;
  onApply: (filters: { customerIds: string[]; fromDate: Date | null }) => void;
  onClear: () => void;
  isCustomised: boolean;
  inHeader?: boolean;
}

export function ApprovedResultsFilterPopover({
  customerIds,
  fromDate,
  onApply,
  onClear,
  isCustomised,
  inHeader = false,
}: ApprovedResultsFilterPopoverProps) {
  const { customers } = useCustomers();
  const [open, setOpen] = useState(false);

  // Draft state — initialised from props when popover opens
  const [draftCustomerIds, setDraftCustomerIds] = useState<string[]>([]);
  const [draftFromDate, setDraftFromDate] = useState<Date | null>(null);

  // Sync draft state from props when popover opens
  useEffect(() => {
    if (open) {
      setDraftCustomerIds([...customerIds]);
      setDraftFromDate(fromDate);
    }
  }, [open, customerIds, fromDate]);

  const toggleCustomer = (customerId: string) => {
    setDraftCustomerIds((prev) =>
      prev.includes(customerId) ? prev.filter((id) => id !== customerId) : [...prev, customerId],
    );
  };

  const handleApply = () => {
    onApply({
      customerIds: draftCustomerIds,
      fromDate: draftFromDate,
    });
    setOpen(false);
  };

  const handleClear = () => {
    onClear();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={inHeader ? "ghostOnPrimary" : "outline"} size="icon" aria-label="Filter" className="relative">
          <Filter className="h-4 w-4" />
          {isCustomised && (
            <span
              data-testid="filter-dot"
              className={`absolute -top-1 -right-1 h-2 w-2 rounded-full ${inHeader ? "bg-white" : "bg-primary"}`}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Customers</label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {customers.map((customer) => (
                <label key={customer.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={draftCustomerIds.includes(customer.id)}
                    onCheckedChange={() => toggleCustomer(customer.id)}
                  />
                  <span className="text-sm">{customer.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Classified since</label>
            <Calendar
              mode="single"
              selected={draftFromDate ?? undefined}
              onSelect={(date) => setDraftFromDate(date ?? null)}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={handleClear}>
              Clear
            </Button>
            <Button size="sm" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
